import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { InlineBanner } from "components/ui/inline-banner";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useIsMultiRegionUser } from "store/selectors/hooks";
import {
    BusinessCard,
    ContactSupportToChange,
    NotStartedCard,
    UncoveredOfficesCard,
} from "./business-verification-cards";
import {
    useBusinessProfiles,
    useCoveredOfficesPhrase,
    useOfficeName,
} from "./use-business-verification";

export const BusinessVerificationPage: React.FC = () => {
    const isMultiOffice = useIsMultiRegionUser();
    const officeName = useOfficeName();
    const coveredOffices = useCoveredOfficesPhrase();
    const { profilesQuery, uncoveredOfficesQuery } = useBusinessProfiles();
    const profiles = profilesQuery.data ?? [];
    const uncoveredOfficeIds = uncoveredOfficesQuery.data ?? [];
    const loading = profilesQuery.isLoading || uncoveredOfficesQuery.isLoading;

    return (
        <ContentFull>
            <div className="space-y-4">
                <ContentHeader>SMS &amp; Voice Business Verification</ContentHeader>
                <ContentSubheader>
                    Verifying your business keeps your calls from being marked as spam and
                    your texts from being blocked.
                </ContentSubheader>
                {profilesQuery.isError || uncoveredOfficesQuery.isError ? (
                    <RetryableErrorBanner
                        message="We couldn't load your businesses."
                        retrying={
                            profilesQuery.isFetching || uncoveredOfficesQuery.isFetching
                        }
                        onRetry={() => {
                            profilesQuery.refetch().catch((): void => undefined);
                            uncoveredOfficesQuery.refetch().catch((): void => undefined);
                        }}
                    />
                ) : null}
                {loading ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4">
                        {profiles.length > 0 ? (
                            <InlineBanner type="info">
                                <ContactSupportToChange />
                            </InlineBanner>
                        ) : null}
                        {profiles.map(profile => (
                            <BusinessCard
                                key={profile.id}
                                profile={profile}
                                isMultiOffice={isMultiOffice}
                                coveredOffices={coveredOffices}
                            />
                        ))}
                        {uncoveredOfficeIds.length === 0 ? null : isMultiOffice ? (
                            <UncoveredOfficesCard
                                officeNames={uncoveredOfficeIds.map(officeName)}
                            />
                        ) : (
                            <NotStartedCard />
                        )}
                    </div>
                )}
            </div>
        </ContentFull>
    );
};
